import React, { useState, useEffect, useCallback } from 'react';
import { Save, RefreshCw, Eye, EyeOff, HelpCircle } from 'lucide-react';
import api from '../../api/axios';
import { useToast } from '../Toast';

const VARIABLES = [
  { key: '{{prenom}}',        desc: 'Prénom du client'           },
  { key: '{{nom}}',           desc: 'Nom du client'              },
  { key: '{{salon}}',         desc: 'Nom de votre salon'         },
  { key: '{{date_estimee}}',  desc: 'Date estimée du prochain passage' },
  { key: '{{service_line}}',  desc: 'Service habituel du client' },
  { key: '{{telephone}}',     desc: 'Téléphone du salon'         },
];

const DEFAULT_TEMPLATE = {
  sujet: 'On pense à vous, {{prenom}} ! C\'est bientôt l\'heure de votre passage',
  corps: `Bonjour {{prenom}},

Nous espérons que vous allez bien ! Cela fait un moment que nous n'avons pas eu le plaisir de vous accueillir chez {{salon}}.

Votre prochain passage est estimé aux alentours du {{date_estimee}}.

Nous serions ravis de vous retrouver pour prendre soin de vous comme vous le méritez.

Pour confirmer ou choisir une autre date, cliquez sur le bouton ci-dessous.

À très bientôt,
L'équipe {{salon}}
{{telephone}}`,
};

export default function EmailTemplate() {
  const showToast = useToast();
  const [form,    setForm]    = useState({ sujet: '', corps: '' });
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [preview, setPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/rappels/template');
      setForm({
        sujet: data.data?.sujet || DEFAULT_TEMPLATE.sujet,
        corps: data.data?.corps || DEFAULT_TEMPLATE.corps,
      });
    } catch {
      setForm(DEFAULT_TEMPLATE);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const insertVar = (variable) => {
    const el = document.getElementById('corps-textarea');
    if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const before = form.corps.slice(0, s);
    const after  = form.corps.slice(e);
    setForm(f => ({ ...f, corps: before + variable + after }));
    setTimeout(() => { el.focus(); el.selectionStart = el.selectionEnd = s + variable.length; }, 0);
  };

  const handleSave = async () => {
    if (!form.sujet.trim() || !form.corps.trim())
      return showToast('L\'objet et le corps sont requis', 'error');
    setSaving(true);
    try {
      await api.put('/rappels/template', form);
      showToast('Modèle enregistré avec succès', 'success');
    } catch { showToast('Erreur lors de la sauvegarde', 'error'); }
    finally { setSaving(false); }
  };

  const renderPreview = (text) => {
    const samples = {
      '{{prenom}}':       'Aissatou',
      '{{nom}}':          'Diallo',
      '{{salon}}':        'SamaSalon',
      '{{date_estimee}}': '25 juillet 2026',
      '{{service_line}}': 'Coiffure & Soin',
      '{{telephone}}':    '+221 77 000 00 00',
    };
    return Object.entries(samples).reduce((s, [k, v]) => s.replaceAll(k, `<mark class="bg-indigo-100 text-indigo-700 rounded px-0.5">${v}</mark>`), text);
  };

  if (loading) return (
    <div className="space-y-4">
      {[1,2,3].map(i => <div key={i} className="h-12 rounded-xl bg-slate-100 animate-pulse" />)}
    </div>
  );

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h3 className="font-bold text-slate-800">Modèle d'email de rappel</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Personnalisez le message envoyé à vos clients. Utilisez les variables ci-dessous pour personnaliser chaque email.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPreview(p => !p)}
            className="flex items-center gap-2 px-3.5 py-2 text-sm rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition font-medium">
            {preview ? <EyeOff size={13} /> : <Eye size={13} />}
            {preview ? 'Modifier' : 'Aperçu'}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 disabled:opacity-50 transition">
            <Save size={13} />
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Variables */}
      <div className="bg-slate-50 rounded-2xl p-4">
        <div className="flex items-center gap-1.5 mb-3">
          <HelpCircle size={13} className="text-indigo-400" />
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Variables disponibles - cliquez pour insérer</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {VARIABLES.map(v => (
            <button key={v.key} onClick={() => !preview && insertVar(v.key)} disabled={preview}
              className="group relative flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-xs font-mono text-indigo-600 hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-default transition-all shadow-sm">
              {v.key}
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-44 bg-slate-800 text-white text-xs rounded-lg px-2.5 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none whitespace-normal text-left leading-relaxed">
                {v.desc}
              </span>
            </button>
          ))}
        </div>
      </div>

      {preview ? (
        /* Aperçu */
        <div className="border border-slate-200 rounded-2xl overflow-hidden">
          <div className="bg-slate-100 px-5 py-3 border-b border-slate-200">
            <p className="text-xs text-slate-400 uppercase font-bold mb-0.5">Objet</p>
            <p className="text-sm font-semibold text-slate-800"
               dangerouslySetInnerHTML={{ __html: renderPreview(form.sujet) }} />
          </div>
          <div className="px-5 py-4 bg-white">
            <p className="text-xs text-slate-400 uppercase font-bold mb-2">Corps</p>
            <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed"
                 dangerouslySetInnerHTML={{ __html: renderPreview(form.corps) }} />
          </div>
          <div className="bg-indigo-50 px-5 py-3 border-t border-slate-200">
            <p className="text-xs text-slate-400 mb-2 font-semibold">Les boutons qui apparaîtront dans l'email :</p>
            <div className="flex gap-3 flex-wrap">
              <span className="inline-block px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold">✓ Je confirme ma venue</span>
              <span className="inline-block px-4 py-2 rounded-xl bg-slate-600 text-white text-xs font-bold">📅 Je préfère une autre date</span>
              <span className="inline-block px-4 py-2 rounded-xl bg-red-500 text-white text-xs font-bold">✕ Je ne viendrai pas</span>
            </div>
          </div>
        </div>
      ) : (
        /* Édition */
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Objet de l'email *</label>
            <input value={form.sujet} onChange={e => setForm(f => ({ ...f, sujet: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition" />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Corps de l'email *</label>
            <textarea id="corps-textarea" rows={14} value={form.corps}
              onChange={e => setForm(f => ({ ...f, corps: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-mono text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition resize-y" />
          </div>
          <div className="bg-indigo-50 rounded-xl p-3.5">
            <p className="text-xs text-indigo-600 font-semibold">
              Les boutons de confirmation (Confirmer / Autre date / Ne pas venir) sont ajoutés automatiquement en bas de chaque email. Vous n'avez pas à les écrire.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
